/**
 * Updates the recently viewed products in localStorage.
 *
 * Superkraftmat-avvik fra Horizon (2026-09-21): lesing og skriving er sperret til
 * samtykket til preferanser er bekreftet gjennom samtykkeporten
 * (assets/skm-samtykke.js, koblet i snippets/scripts.liquid). Uten bekreftelse
 * svarer getProducts() tomt og addProduct() gjør ingenting, så predictive search
 * verken leser gammel lagring eller sender id-ene i en søkeforespørsel før
 * avgjørelsen er kjent. clearProducts() virker alltid, og rører bare denne
 * nøkkelen. Sikkerhetsfunn 1, 2026-09-21.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  /** @static {boolean} Om samtykket til preferanser er bekreftet. Fail-closed: false til noen sier fra. */
  static #tillatt = false;

  /** Åpner lesing og skriving. Kalles av samtykkeporten når preferanser er tillatt. */
  static tillat() {
    this.#tillatt = true;
  }

  /** Sperrer lesing og skriving, og tømmer lagringen. Kalles når preferanser ikke er tillatt. */
  static sperr() {
    this.#tillatt = false;
    this.clearProducts();
  }

  /** @returns {boolean} */
  static get erTillatt() {
    return this.#tillatt;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!this.#tillatt) return;
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products. Empty until consent is confirmed.
   */
  static getProducts() {
    if (!this.#tillatt) return [];
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}
